import React from "react";
import HeadTittle from "../../../../components/HeadTittle";
import DivContainerBody from "../../../../components/DivContainerBody";
import StepperTabs from "../../../../components/StepperTabs/StepperTabs";
import StepTab from "../../../../components/StepperTabs/StepTab";
import ButtonAction from "../../../../components/buttons/ButtonAction";
import {EditIcon} from "../../../../components/buttons/IconButtons";
import CustomPhotoPicker from "../../../../components/PhotoPicker/CustomPhotoPicker";
import {SUGGESTION_MODE_OPTION} from "../../../../components/PhotoPicker/SubComponent/Models/Models";
import FullPageLoader from "../../../../utils/FullPageLoader";
import {ACTION_MODEL} from "../../../../Helpers/ActionModel";
import {FILE_TYPE} from "../../../../Helpers/fileType";
import routes from "../../../../Routes/routes";
import {permissionObj} from "../../../../Helpers/permission";
import Step1Details from "../../Company/Employees/EnrollmentSteps/Step1Details";
import FingerprintStep from "../../Company/Employees/EnrollmentSteps/FingerprintStep";
import SummaryFingerprintCard from "../../Company/Employees/EnrollmentSteps/SubComponents/SummaryFingerprintCard";
import FingerPrintSpinner from "../../Company/Employees/Modal/FingerPrintSpinner";
import CopySecretModal from "../../Company/Employees/Modal/CopySecretModal";
import UpdateGuardApp from "../Functionality/UpdateGuardApp";
import {GUARD_UPDATE_TAB_LABELS, GUARD_UPDATE_TABS} from "../Model/Model";
import GuardContractStep from "./Steps/GuardContractStep";
import GuardDocumentsStep from "./Steps/GuardDocumentsStep";

/**
 * Profile of a guard. The details, the contract, the photo and the documents added are saved with
 * the update button, the fingerprints and the deleted documents as soon as they change.
 */
const UpdateGuard = () => {
    const guard = UpdateGuardApp();
    const {
        t, navigate, canUpdate, canRead, disabled, activeStep, setActiveStep, handleStepChange,
        loadingProgress, capturing, setCapturing, handleCloseModal,
        userData, setUserData, handleFormChangeUserData, emptyFields, setEmptyFields,
        isValidEmail, isValidDocumentId, isValidPhoneNumber, isValidDob, setIsValidDob, isValidPin,
        isValidPassword,
        employeeData, handleFormChangeEmployeeData, isValidEndDate, setIsValidEndDate,
        zoneAccesses, zoneAccessIds, setZoneAccessIds,
        documents, addDocument, removeDocument, downloadDocument, savePendingDocuments,
        photoPickerFile, setPhotoPickerFile, emptySelfie, isPilgrimsValidation,
        setSelectedReader,
        capturedFingerprint, setCapturedFingerprint, fingerprintSrc, setFingerprintSrc, fingerprintsList,
        fingerprintsTemp, setFingerprintsTemp, mergedFingerprints, handleUploadFinger, fingerprintName,
        setFingerprintName, setUpdated, updatedFinger, setModified,
        handleUpdateGuard, handleUpdatePhoto, secretModal, setSecretModal, secretKey, setSecretKey,
    } = guard;

    // Without the update permission the guard is shown read-only, as its details
    if (!canUpdate && !canRead) navigate(routes.forbidden);

    const steps = GUARD_UPDATE_TAB_LABELS.map((label) => t(label));

    /**
     * Saves the details, the contract, the new photo and the documents added. When the details or
     * the contract are not valid, nothing is saved and their tab is opened.
     */
    const handleUpdate = () => {
        const validDetails = userData.isValid() && isValidEmail && isValidPhoneNumber && isValidDob
            && isValidDocumentId && isValidPin && isValidPassword;
        const validContract = employeeData.isValid() && isValidEndDate;
        setEmptyFields(!(validDetails && validContract));
        if (!validDetails || !validContract) {
            setActiveStep(validDetails ? GUARD_UPDATE_TABS.CONTRACT : GUARD_UPDATE_TABS.DETAILS);
            return;
        }
        // The guard is read again once, after its details and its photo are saved
        Promise.all([handleUpdateGuard(), handleUpdatePhoto()]).then(() => setUpdated(true));
        savePendingDocuments();
    };

    return (
        <>
            {loadingProgress && <FullPageLoader/>}
            {capturing && <FingerPrintSpinner onClose={handleCloseModal} capturing={capturing}/>}
            <HeadTittle link={routes.guards.list} name={canUpdate ? 'update' : 'details'}>
                {canUpdate &&
                <ButtonAction
                    id='btn_ug_update'
                    name={'update'}
                    className={'btn-icon-edit'}
                    Icon={EditIcon}
                    onClick={handleUpdate}
                />
                }
            </HeadTittle>

            <DivContainerBody>
                <StepperTabs
                    onlyTab={true}
                    disabled={disabled}
                    steps={steps}
                    activeStep={activeStep}
                    isStepOptional={() => false}
                    setActiveStep={setActiveStep}
                    handleStepChange={handleStepChange}
                >
                    <StepTab index={GUARD_UPDATE_TABS.DETAILS} activeStep={activeStep} normalHeight={true}>
                        {/* The details carry their own top margin, it is reduced to the one of the other tabs */}
                        <div className={'mt-n2'}>
                            <Step1Details
                                userData={userData}
                                onChange={handleFormChangeUserData}
                                emptyFields={emptyFields}
                                isValidEmail={isValidEmail}
                                isValidDocumentId={isValidDocumentId}
                                isValidPhoneNumber={isValidPhoneNumber}
                                isValidDob={isValidDob}
                                setIsValidDob={setIsValidDob}
                                isValidPin={isValidPin}
                                managePasswordsPermission={permissionObj.WEB_GUARD_CAN_MANAGE_PASSWORDS}
                                disable={disabled}
                            />
                        </div>
                    </StepTab>

                    <StepTab index={GUARD_UPDATE_TABS.CONTRACT} activeStep={activeStep} normalHeight={true}>
                        <GuardContractStep
                            employeeData={employeeData}
                            onChange={handleFormChangeEmployeeData}
                            emptyFields={emptyFields}
                            isValidEndDate={isValidEndDate}
                            setIsValidEndDate={setIsValidEndDate}
                            zoneAccesses={zoneAccesses}
                            zoneAccessIds={zoneAccessIds}
                            setZoneAccessIds={setZoneAccessIds}
                            disabled={disabled}
                        />
                    </StepTab>

                    <StepTab index={GUARD_UPDATE_TABS.DOCUMENTS} activeStep={activeStep} normalHeight={true}>
                        <GuardDocumentsStep
                            documents={documents}
                            addDocument={addDocument}
                            removeDocument={removeDocument}
                            downloadDocument={downloadDocument}
                            disabled={disabled}
                        />
                    </StepTab>

                    <StepTab index={GUARD_UPDATE_TABS.PHOTO} activeStep={activeStep} normalHeight={true}>
                        <div className={'mt-3'}>
                            {activeStep === GUARD_UPDATE_TABS.PHOTO &&
                                <CustomPhotoPicker
                                    id='fp_ug_update_photo_profile'
                                    photoPicker={photoPickerFile}
                                    setPhotoPicker={setPhotoPickerFile}
                                    actionModel={ACTION_MODEL.UPDATE}
                                    accept={{...FILE_TYPE.IMAGE_FACE_VALIDATION}}
                                    showBtnCamera={true}
                                    validateFace={true}
                                    emptyFields={emptySelfie}
                                    classWith={'content-center-w-400px'}
                                    showPreview={true}
                                    disabled={disabled}
                                    required={isPilgrimsValidation}
                                    typeSuggestions={SUGGESTION_MODE_OPTION.SELFIE}
                                />
                            }
                        </div>
                    </StepTab>

                    <StepTab index={GUARD_UPDATE_TABS.FINGERPRINT} activeStep={activeStep} normalHeight={true}>
                        <div className={'mt-3'}>
                            {/* Without the update permission the fingerprints are only listed */}
                            {canUpdate ?
                                <FingerprintStep
                                    t={t}
                                    capturing={capturing}
                                    setCapturing={setCapturing}
                                    capturedFingerprint={capturedFingerprint}
                                    setCapturedFingerprint={setCapturedFingerprint}
                                    fingerprintSrc={fingerprintSrc}
                                    setFingerprintSrc={setFingerprintSrc}
                                    updatedFinger={updatedFinger}
                                    setFingerprintName={setFingerprintName}
                                    setSelectedReader={setSelectedReader}
                                    fingerprintsList={fingerprintsList}
                                    setUpdated={setUpdated}
                                    setModified={setModified}
                                    fingerprintName={fingerprintName}
                                    handleUploadFinger={handleUploadFinger}
                                    userData={userData}
                                    fingerprintsTemp={fingerprintsTemp}
                                    setFingerprintsTemp={setFingerprintsTemp}
                                    mergedFingerprints={mergedFingerprints}
                                    emptyFields={emptyFields}
                                    setEmptyFields={setEmptyFields}
                                />
                                :
                                <SummaryFingerprintCard
                                    mergedFingerprints={fingerprintsList}
                                    emptyText={'no_fingerprints_added_yet'}
                                />
                            }
                        </div>
                    </StepTab>
                </StepperTabs>
            </DivContainerBody>

            <CopySecretModal
                show={secretModal}
                setShow={setSecretModal}
                secret={secretKey}
                setSecret={setSecretKey}
                setData={setUserData}
                generatedPassword={null}
                onHide={() => {}}
                closeText={'continue'}
            />
        </>
    );
};

export default UpdateGuard;
